package org.example.project.screens_cus_mobile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginekalender.EngineMonthCalendar
import org.example.project.enginekalender.kalenderBookingMarkerColorHex
import org.example.project.enginekalender.kalenderDayState
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.enginekalender.loadKalenderBookings
import org.example.project.enginekalender.parseKalenderIsoDate
import org.example.project.enginepersonal.ZeitApi
import org.example.project.enginepersonal.ZeitMobileDayPopup
import org.example.project.enginepersonal.ZeitMonth
import org.example.project.enginepersonal.ZeitMobileDayInfo
import org.example.project.enginepersonal.ZeitMobileMonthSummary
import org.example.project.enginepersonal.ZeitprofilConfig
import org.example.project.enginepersonal.UrlaubsuebersichtDayPopup
import org.example.project.enginepersonal.defaultZeitprofil
import org.example.project.enginepersonal.zeitMobileDayInfo
import org.example.project.enginepersonal.zeitMobileProfileHourRange
import org.example.project.enginepersonal.zeitMobileMonthLabel
import org.example.project.enginepersonal.zeitMobileMonthSummary
import org.example.project.manager.NodeScope
import org.example.project.screens_cus.Abwesenheitsbedarf
import org.example.project.screens_cus.UrlaubsApi
import org.example.project.screens_cus.ZeitkontoJournalEintrag
import org.example.project.service.LoginService
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent
import kotlin.js.Date
import kotlinx.browser.window
internal enum class MitarbeiterUrlaubStatusGroup(val label: String) {
    OFFEN("Offen"),
    GENEHMIGT("Genehmigt"),
    STORNIERT("Storniert")
}
internal fun mitarbeiterShiftMonth(month: String, delta: Int): String {
    val parts = month.split("-")
    val year = parts.getOrNull(0)?.toIntOrNull() ?: Date().getFullYear().toInt()
    val monthNumber = parts.getOrNull(1)?.toIntOrNull() ?: Date().getMonth().toInt() + 1
    val next = Date(year, monthNumber - 1 + delta, 1)
    return "${next.getFullYear().toInt()}-${(next.getMonth().toInt() + 1).toString().padStart(2, '0')}"
}

internal fun mitarbeiterCurrentZeitMonth(): ZeitMonth {
    val now = Date()
    return ZeitMonth(now.getFullYear().toInt(), now.getMonth().toInt() + 1)
}

internal fun mitarbeiterZeitMonthFromIso(iso: String): ZeitMonth {
    val parts = iso.split("-")
    return ZeitMonth(
        year = parts.getOrNull(0)?.toIntOrNull() ?: Date().getFullYear().toInt(),
        month = parts.getOrNull(1)?.toIntOrNull() ?: (Date().getMonth().toInt() + 1)
    )
}

internal fun mitarbeiterShiftIsoDate(iso: String, delta: Int): String {
    val date = mitarbeiterDateFromIso(iso)
    val next = Date(date.getFullYear().toInt(), date.getMonth().toInt(), date.getDate().toInt() + delta)
    return "${next.getFullYear().toInt()}-" +
        "${(next.getMonth().toInt() + 1).toString().padStart(2, '0')}-" +
        next.getDate().toInt().toString().padStart(2, '0')
}

internal fun mitarbeiterMonthLabel(month: String): String {
    val parts = month.split("-")
    val year = parts.getOrNull(0).orEmpty()
    val monthNumber = parts.getOrNull(1)?.toIntOrNull() ?: 1
    val label = listOf("Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember")
        .getOrElse(monthNumber - 1) { monthNumber.toString() }
    return "$label $year"
}

internal fun mitarbeiterMonthCells(month: String): List<String> {
    val parts = month.split("-")
    val year = parts.getOrNull(0)?.toIntOrNull() ?: Date().getFullYear().toInt()
    val monthNumber = parts.getOrNull(1)?.toIntOrNull() ?: Date().getMonth().toInt() + 1
    val first = Date(year, monthNumber - 1, 1)
    val offset = (first.getDay().toInt() + 6) % 7
    val days = Date(year, monthNumber, 0).getDate().toInt()
    return List(offset) { "" } + (1..days).map { day ->
        "$year-${monthNumber.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
    }
}

internal fun mitarbeiterWeekDays(iso: String): List<String> {
    val selected = mitarbeiterDateFromIso(iso)
    val mondayOffset = (selected.getDay().toInt() + 6) % 7
    val monday = Date(
        selected.getFullYear().toInt(),
        selected.getMonth().toInt(),
        selected.getDate().toInt() - mondayOffset
    )
    return (0..6).map { offset ->
        val day = Date(
            monday.getFullYear().toInt(),
            monday.getMonth().toInt(),
            monday.getDate().toInt() + offset
        )
        "${day.getFullYear().toInt()}-" +
            "${(day.getMonth().toInt() + 1).toString().padStart(2, '0')}-" +
            day.getDate().toInt().toString().padStart(2, '0')
    }
}

internal fun mitarbeiterDateFromIso(iso: String): Date {
    val parts = iso.split("-")
    return Date(
        parts.getOrNull(0)?.toIntOrNull() ?: Date().getFullYear().toInt(),
        (parts.getOrNull(1)?.toIntOrNull() ?: 1) - 1,
        parts.getOrNull(2)?.toIntOrNull() ?: 1
    )
}

@Composable
internal fun MitarbeiterMetric(label: String, value: String) {
    Div({ classes(MitarbeiterMobileStyles.metricItem) }) {
        Span({ classes(MitarbeiterMobileStyles.metricLabel) }) { Text(label) }
        Span({ classes(MitarbeiterMobileStyles.metricValue) }) { Text(value) }
    }
}

@Composable
internal fun MitarbeiterDateField(label: String, value: String, minValue: String = "", onValue: (String) -> Unit) {
    Div({ classes(MitarbeiterMobileStyles.dateField) }) {
        Span({ classes(MitarbeiterMobileStyles.infoLabel) }) { Text(label) }
        Input(InputType.Date, attrs = {
            value(value)
            if (minValue.isNotBlank()) attr("min", minValue)
            onInput { onValue(it.value) }
            classes(MitarbeiterMobileStyles.dateInput)
        })
    }
}

@Composable
internal fun MitarbeiterTerminSlot(termin: All) {
    val markerColor = kalenderBookingMarkerColorHex(listOf(termin)) ?: "#5b9bff"
    Div({
        classes(MitarbeiterMobileStyles.slot)
        style {
            property("background", "color-mix(in srgb, $markerColor 20%, transparent)")
            property("border", "1px solid color-mix(in srgb, $markerColor 42%, transparent)")
        }
    }) {
        Span({ classes(MitarbeiterMobileStyles.slotTitle) }) {
            Text(mitarbeiterTerminTitel(termin))
        }
        Span({ classes(MitarbeiterMobileStyles.slotTime) }) {
            Text(
                listOf(termin.uhrzeitVon, termin.uhrzeitBis)
                    .filter { it.isNotBlank() }
                    .joinToString(" - ")
                    .ifBlank { "ganztags" }
            )
        }
        val detail = termin.ort.ifBlank { termin.beschreibung.orEmpty() }
        if (detail.isNotBlank()) {
            Span({ classes(MitarbeiterMobileStyles.slotDetail) }) { Text(detail) }
        }
    }
}

@Composable
internal fun MitarbeiterTerminInfo(label: String, value: String) {
    Div({ classes(MitarbeiterMobileStyles.infoRow) }) {
        Span({ classes(MitarbeiterMobileStyles.infoLabel) }) { Text(label) }
        Span({ classes(MitarbeiterMobileStyles.infoValue) }) { Text(value) }
    }
}

@Composable
internal fun MitarbeiterKalenderHinweis(text: String, error: Boolean = false) {
    Div({
        classes(
            if (error) MitarbeiterMobileStyles.error
            else MitarbeiterMobileStyles.notice
        )
    }) { Text(text) }
}

internal fun mitarbeiterKalenderDatum(value: String): String {
    val raw = value.trim()
    if (parseKalenderIsoDate(raw) != null) return raw
    val match = Regex("""^(\d{2})\.(\d{2})\.(\d{4})$""").matchEntire(raw) ?: return raw
    val (day, month, year) = match.destructured
    return "$year-$month-$day"
}

internal fun mitarbeiterTerminBelegtStunde(termin: All, hour: Int): Boolean {
    val von = mitarbeiterZeitMinuten(termin.uhrzeitVon).takeIf { it >= 0 } ?: 0
    val bis = mitarbeiterZeitMinuten(termin.uhrzeitBis).takeIf { it > von } ?: (24 * 60)
    val slotVon = hour * 60
    val slotBis = (hour + 1) * 60
    return von < slotBis && bis > slotVon
}

internal fun mitarbeiterTerminStartetInStunde(termin: All, hour: Int, firstVisibleHour: Int): Boolean {
    val von = mitarbeiterZeitMinuten(termin.uhrzeitVon)
    if (von < 0) return hour == firstVisibleHour
    val startHour = (von / 60).coerceIn(0, 23)
    return startHour == hour || (startHour < firstVisibleHour && hour == firstVisibleHour)
}

internal fun mitarbeiterArbeitsstundenRange(termine: List<All>): IntRange? {
    val ranges = termine.mapNotNull { termin ->
        val start = mitarbeiterZeitMinuten(termin.uhrzeitVon)
        val end = mitarbeiterZeitMinuten(termin.uhrzeitBis)
        if (start < 0 || end <= start) return@mapNotNull null
        start to end
    }
    if (ranges.isEmpty()) return null
    val firstHour = (ranges.minOf { it.first } / 60).coerceIn(0, 23)
    val lastHour = ((ranges.maxOf { it.second } - 1) / 60).coerceIn(firstHour, 23)
    return firstHour..lastHour
}

internal fun mitarbeiterIstGanztagsUrlaub(termin: All): Boolean =
    mitarbeiterIstAbwesenheit(termin) &&
        mitarbeiterZeitMinuten(termin.uhrzeitVon) == 0 &&
        mitarbeiterZeitMinuten(termin.uhrzeitBis) == 24 * 60

private fun mitarbeiterIstAbwesenheit(termin: All): Boolean {
    if (termin.urlaubstag == false) return false
    val labels = listOf(termin.art, termin.abwesenheitsart, termin.bezeichnung)
        .map { it.trim().lowercase() }
        .filter { it.isNotBlank() }
    val abwesenheiten = setOf(
        "urlaub",
        "freizeitausgleich",
        "unbezahlt frei",
        "sonderurlaub",
        "krankheit",
        "homeoffice",
        "fehltag"
    )
    return termin.urlaub || termin.urlaubId.isNotBlank() || labels.any { it in abwesenheiten }
}

internal fun mitarbeiterUrlaubLabel(termin: All): String =
    "${mitarbeiterAbwesenheitsart(termin)} ${mitarbeiterAbwesenheitsStatusText(termin)}"

internal fun mitarbeiterTerminTitel(termin: All): String =
    if (mitarbeiterIstGanztagsUrlaub(termin)) {
        mitarbeiterAbwesenheitsart(termin)
    } else {
        termin.bezeichnung.ifBlank { termin.art.ifBlank { "Termin" } }
    }

internal fun mitarbeiterAbwesenheitsart(termin: All): String =
    termin.abwesenheitsart.ifBlank { termin.bezeichnung.ifBlank { "Urlaub" } }

internal fun mitarbeiterAbwesenheitsStatusText(termin: All): String =
    when (ZustandManager.Urlaub.fromCode(termin.zustand)) {
        ZustandManager.Urlaub.BEANTRAGT -> "beantragt"
        ZustandManager.Urlaub.IN_BEARBEITUNG -> "in Bearbeitung"
        ZustandManager.Urlaub.GENEHMIGT -> "genehmigt"
        ZustandManager.Urlaub.VERWEIGERT -> "abgelehnt"
        ZustandManager.Urlaub.STORNIERT -> "storniert"
        else -> ""
    }

internal fun mitarbeiterUrlaubStatusGroup(antrag: All): MitarbeiterUrlaubStatusGroup =
    when (ZustandManager.Urlaub.fromCode(antrag.zustand)) {
        ZustandManager.Urlaub.GENEHMIGT -> MitarbeiterUrlaubStatusGroup.GENEHMIGT
        ZustandManager.Urlaub.VERWEIGERT,
        ZustandManager.Urlaub.STORNIERT -> MitarbeiterUrlaubStatusGroup.STORNIERT
        else -> MitarbeiterUrlaubStatusGroup.OFFEN
    }

internal fun mitarbeiterUrlaubZeitraumLabel(von: String, bis: String): String =
    when {
        von.isBlank() && bis.isBlank() -> "-"
        bis.isBlank() || bis == von -> mitarbeiterKalenderDatumsLabel(von)
        von.isBlank() -> mitarbeiterKalenderDatumsLabel(bis)
        else -> "${mitarbeiterKalenderDatumsLabel(von)} bis ${mitarbeiterKalenderDatumsLabel(bis)}"
    }

internal fun mitarbeiterZeitMinuten(value: String): Int {
    val parts = value.trim().split(":")
    val hour = parts.getOrNull(0)?.toIntOrNull() ?: return -1
    val minute = parts.getOrNull(1)?.toIntOrNull() ?: 0
    return when {
        hour in 0..23 && minute in 0..59 -> hour * 60 + minute
        hour == 24 && minute == 0 -> 24 * 60
        else -> -1
    }
}

internal fun mitarbeiterKalenderDatumsLabel(datum: String): String {
    val parts = datum.split("-")
    return if (parts.size == 3) "${parts[2]}.${parts[1]}." else datum
}

internal fun mitarbeiterIsoLessThan(left: String, right: String): Boolean =
    parseKalenderIsoDate(left) != null && parseKalenderIsoDate(right) != null && left < right

internal fun mitarbeiterKalenderWochentag(datum: String): String {
    val parsed = parseKalenderIsoDate(datum) ?: return ""
    val date = Date(
        parsed.year,
        parsed.month - 1,
        parsed.day
    )
    return listOf("So", "Mo", "Di", "Mi", "Do", "Fr", "Sa")
        .getOrElse(date.getDay().toInt()) { "" }
}

internal fun mitarbeiterKalenderWerktage(von: String, bis: String): List<String> {
    val start = parseKalenderIsoDate(von) ?: return emptyList()
    val end = parseKalenderIsoDate(bis) ?: return emptyList()
    val startDate = Date(
        start.year.takeIf { it > 0 } ?: return emptyList(),
        (start.month.takeIf { it > 0 } ?: return emptyList()) - 1,
        start.day.takeIf { it > 0 } ?: return emptyList()
    )
    val endDate = Date(
        end.year.takeIf { it > 0 } ?: return emptyList(),
        (end.month.takeIf { it > 0 } ?: return emptyList()) - 1,
        end.day.takeIf { it > 0 } ?: return emptyList()
    )
    if (startDate.getTime() > endDate.getTime()) return emptyList()
    val result = mutableListOf<String>()
    val cursor = Date(startDate.getTime())
    while (cursor.getTime() <= endDate.getTime()) {
        val weekday = cursor.getDay().toInt()
        if (weekday != 0 && weekday != 6) {
            val y = cursor.getFullYear().toInt()
            val m = (cursor.getMonth().toInt() + 1).toString().padStart(2, '0')
            val d = cursor.getDate().toInt().toString().padStart(2, '0')
            result += "$y-$m-$d"
        }
        cursor.asDynamic().setDate(cursor.getDate() + 1)
    }
    return result
}

internal fun mitarbeiterVacationForDay(vacations: List<All>, iso: String): All? =
    vacations.firstOrNull { vacation ->
        val explicitDays = vacation.tage
        if (explicitDays.isNotEmpty()) {
            iso in explicitDays
        } else {
            val von = vacation.datumVon.ifBlank { vacation.datum }
            val bis = vacation.datumBis.ifBlank { von }
            von.isNotBlank() && bis.isNotBlank() && iso >= von && iso <= bis
        }
    }

internal fun mitarbeiterUrlaubstageText(value: Double): String =
    if (value % 1.0 == 0.0) value.toInt().toString() else value.toString().replace(".", ",")

internal fun mitarbeiterStundenText(value: Double): String =
    "${if (value % 1.0 == 0.0) value.toInt().toString() else value.toString().replace(".", ",")} Std."

internal fun mitarbeiterJournalDayHours(entries: List<ZeitkontoJournalEintrag>): Double {
    val direct = entries.map { it.arbeitsstunden }.filter { it != 0.0 }
    if (direct.isNotEmpty()) return direct.maxOrNull() ?: 0.0
    val deltas = entries.map { mitarbeiterJournalEntryHours(it) }.filter { it != 0.0 }
    return deltas.fold(0.0) { acc, value -> acc + value }
}

internal fun mitarbeiterJournalEntryHours(entry: ZeitkontoJournalEintrag): Double =
    when {
        entry.arbeitsstunden != 0.0 -> entry.arbeitsstunden
        entry.stunden != 0.0 -> entry.stunden
        entry.stundenDelta != 0.0 -> entry.stundenDelta
        else -> 0.0
    }

internal fun mitarbeiterEntryValue(entry: ZeitkontoJournalEintrag, type: String): String {
    val haystack = listOf(entry.event, entry.buchungsart, entry.vorgangsart, entry.buchungstyp)
        .joinToString(" ")
        .lowercase()
    val matches = when (type) {
        "kommen" -> listOf("kommen", "start", "beginn", "ein").any { it in haystack }
        "gehen" -> listOf("gehen", "ende", "stop", "aus").any { it in haystack }
        "pause" -> listOf("pause").any { it in haystack }
        else -> false
    }
    return if (matches) entry.buchungsuhrzeit.ifBlank { entry.erstelltUm.ifBlank { "-" } } else "-"
}
